import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useContext, useRef, useState } from 'react';
import { RiCameraLensAiLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { FieldNumber } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import LabelGroup from '@/components/ui/LabelGroup';
import { MessageContext } from '@/helpers/MessageProvider';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const dispatch = useDispatch();
  const [isProcessing, setProcessing] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const inputRefs = useRef({
    startWaypoint: useRef(),
    endWaypoint: useRef(),
    captureInterval: useRef(),
  }).current;
  const { publishEvent } = useContext(MessageContext);
  const missions = useSelector((state) => state.missions);

  //#region Process Functions
  const process = (robot) => {
    // Enable 시, 촬영 설정 전송
    if (isEnabled) {
      const commands = missions[robot.id]?.commands;
      if (!commands) {
        onFailure(`No mission loaded for ${robot.name}.`);
        return;
      }

      const startWaypoint = Number(inputRefs.startWaypoint.current.value);
      const endWaypoint = Number(inputRefs.endWaypoint.current.value);
      const captureInterval = Number(inputRefs.captureInterval.current.value);

      // 종료 경로점은 미션 길이보다 작아야 함
      if (endWaypoint >= commands.length) {
        onFailure(
          `End waypoint number must be less than the mission length (${commands.length}) loaded on ${robot.name}.`,
        );
        return;
      }

      publishEvent(robot, 'broadcaster/capture', {
        startWaypoint, // 촬영 시작점 번호
        endWaypoint, // 촬영 종료점 번호
        captureInterval, // 촬영 시간 간격
      });
    }
    // Disable 시 촬영 설정 초기화
    else {
      publishEvent(robot, 'broadcaster/capture/clear');
    }

    onSuccess(robot);
  };
  //#endregion

  //#region Result Handlers
  // 성공 시 처리
  const onSuccess = (robot) => {
    if (isEnabled) {
      notifier.success(`Auto capture enabled successfully on ${robot.name}.`);
    } else {
      notifier.success(`Auto capture disabled successfully on ${robot.name}.`);
    }
    setProcessing(false);
  };

  // 실패 시 처리
  const onFailure = (message) => {
    notifier.error(message);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const handleChange = (e) => {
    setIsEnabled(e.target.value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    inputRefs[name].current.value = Number(value);
  };

  const doCommand = () => {
    if (isEnabled) {
      if (!inputRefs.startWaypoint.current.value) {
        notifier.error('Please enter the start waypoint number.');
        inputRefs.startWaypoint.current.focus();
        return;
      }

      const startWaypoint = Number(inputRefs.startWaypoint.current.value);
      // 시작 경로점은 2 이상이어야 함
      if (startWaypoint < 2) {
        notifier.error('Start waypoint number must be 2 or greater.');
        inputRefs.startWaypoint.current.focus();
        return;
      }

      if (!inputRefs.endWaypoint.current.value) {
        notifier.error('Please enter the end waypoint number.');
        inputRefs.endWaypoint.current.focus();
        return;
      }

      const endWaypoint = Number(inputRefs.endWaypoint.current.value);
      // 시작 경로점은 종료 경로점보다 작아야 함
      if (startWaypoint >= endWaypoint) {
        notifier.error('Start waypoint number must be less than end waypoint number.');
        inputRefs.endWaypoint.current.focus();
        return;
      }

      if (!inputRefs.captureInterval.current.value) {
        notifier.error('Please enter the capture interval.');
        inputRefs.captureInterval.current.focus();
        return;
      }
    }

    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Configure automatic photo capture during mission execution</div>
      <div className={cx('form')}>
        <LabelGroup htmlFor="action" label="Action">
          <Select name="action" value={isEnabled} onChange={handleChange}>
            <option value={false}>Disable</option>
            <option value={true}>Enable</option>
          </Select>
        </LabelGroup>
        {isEnabled && (
          <>
            <LabelGroup htmlFor="startWaypoint" label="Start Waypoint No.">
              <div className={cx('fieldWrapper')}>
                <FieldNumber
                  id="startWaypoint"
                  ref={inputRefs.startWaypoint}
                  name="startWaypoint"
                  onBlur={handleBlur}
                />
              </div>
            </LabelGroup>
            <LabelGroup htmlFor="endWaypoint" label="End Waypoint No.">
              <div className={cx('fieldWrapper')}>
                <FieldNumber id="endWaypoint" ref={inputRefs.endWaypoint} name="endWaypoint" onBlur={handleBlur} />
              </div>
            </LabelGroup>
            <LabelGroup htmlFor="captureInterval" label="Capture Interval">
              <div className={cx('fieldWrapper')}>
                <FieldNumber
                  id="captureInterval"
                  ref={inputRefs.captureInterval}
                  name="captureInterval"
                  defaultValue={5}
                  onBlur={handleBlur}
                />
                <div className={cx('unit')}>sec</div>
              </div>
            </LabelGroup>
          </>
        )}
      </div>
      <ConfirmSlider
        width="100%"
        height={40}
        onConfirm={doCommand}
        isProcessing={isProcessing}
        labelStyle={{ fontSize: 12 }}
      />
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Set Auto Shoot';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = false;
Command.commanderOnly = true;
Command.Icon = RiCameraLensAiLine;
Command.Panel = Panel;

export default Command;
